import { AxiosError, AxiosResponse } from 'axios';
import { FormikErrors, FormikValues } from 'formik';
import { set } from 'lodash';
import { Renderable, toast } from 'react-hot-toast';
import { matchPath } from 'react-router-dom';
import { publicPages } from '@/modules/common/routes.tsx';
import { CloseIcon } from 'ui/icons';

export interface CompliOneApiError {
  error_code: number;
  error_detail: string;
  error_message: string;
  validation_errors?: ValidationErrorDetailItem[];
  detail?: ValidationErrorDetailItem[];
}

export interface ValidationErrorDetailItem {
  loc: string[];
  msg: string;
  type: string;
}

export interface WithErrorHandlingParams {
  loading?: string | Renderable;
  success?: string | Renderable;
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  notificationDuration?: number;
  errorDuration?: number;
  customErrorMessage?: string;
  canClose?: boolean;
  toastId?: string;
}

const defaultErrorHandlingParams = {
  loading: 'Loading...',
  success: 'Success!',
  notificationDuration: 2500,
  errorDuration: 5000,
  canClose: true,
};

export function formatApiErrorMessage(error: AxiosError<CompliOneApiError>): string {
  return error.response?.data?.error_message || error.message || 'Unknown error';
}

export function extractValidationErrors(
  error: AxiosError<CompliOneApiError>,
): FormikErrors<FormikValues> {
  const validationErrors = error.response?.data?.validation_errors ?? error.response?.data?.detail;

  if (
    (error.response?.status === 400 || error.response?.status === 422) &&
    Array.isArray(validationErrors)
  ) {
    return validationErrors.reduce((acc, item) => {
      const key = item.loc.filter(node => node !== 'body').join('.');
      set(acc, key, item.msg);
      return acc;
    }, {} as FormikErrors<FormikValues>);
  }
  return {};
}

export async function withErrorHandling<T>(
  fn: Promise<AxiosResponse<T>>,
  params: WithErrorHandlingParams = {},
): Promise<AxiosResponse<T>> {
  const options = {
    ...params,
    loading: params.loading ?? defaultErrorHandlingParams.loading,
    success: params.success ?? defaultErrorHandlingParams.success,
    notificationDuration:
      params.notificationDuration ?? defaultErrorHandlingParams.notificationDuration,
    errorDuration: params.errorDuration ?? defaultErrorHandlingParams.errorDuration,
    canClose: params.canClose ?? defaultErrorHandlingParams.canClose,
  };
  const toastId = toast.loading(options.loading, { id: options.toastId });
  try {
    const response = await fn;

    if (options.success) {
      toast.success(
        options.canClose
          ? currentToast => (
              <span className="flex items-center gap-3">
                <span>{options.success}</span>
                <button
                  type="button"
                  aria-label="Close notification"
                  className="rounded p-0.5 transition hover:bg-green-100"
                  onClick={() => toast.dismiss(currentToast.id)}
                >
                  <img src={CloseIcon} alt="" className="h-4 w-4 min-w-4" />
                </button>
              </span>
            )
          : options.success,
        {
          id: toastId,
          duration: options.notificationDuration,
        },
      );
    } else {
      toast.dismiss(toastId);
    }

    return response;
  } catch (e) {
    const error = e as AxiosError<CompliOneApiError>;

    let errorMessage: Renderable = <div>{formatApiErrorMessage(error)}</div>;

    if (
      (error.response?.status === 400 || error.response?.status === 422) &&
      options.customErrorMessage
    ) {
      errorMessage = <div>{options.customErrorMessage}</div>;
    }
    if ((error.response?.status === 400 || error.response?.status === 422) && options.setErrors) {
      const validationErrors = extractValidationErrors(error);
      if (Object.keys(validationErrors).length) {
        options.setErrors(validationErrors);
        errorMessage = <div>Validation error occurred, check your input data.</div>;
      }
    }
    const isPublicAccessPage = publicPages.some(path => matchPath(path, location.pathname));
    if (error.response?.status === 418 || (isPublicAccessPage && error.response?.status === 401)) {
      toast.dismiss(toastId);
    } else {
      toast.error(
        options.canClose
          ? currentToast => (
              <span className="flex items-center gap-3">
                <span>{errorMessage}</span>
                <button
                  type="button"
                  aria-label="Close notification"
                  className="rounded p-0.5 transition hover:bg-red-100"
                  onClick={() => toast.dismiss(currentToast.id)}
                >
                  <img src={CloseIcon} alt="" className="h-4 w-4 min-w-4" />
                </button>
              </span>
            )
          : errorMessage,
        {
          id: toastId,
          duration: options.errorDuration,
        },
      );
    }
    throw e;
  }
}
